/**
 * OrgThemeProvider
 *
 * After authentication, fetches the organization's branding config and:
 *  1. Injects a <style> tag that overrides Joy UI CSS variables for the org palette.
 *     Uses `html[data-joy-color-scheme="…"]` selectors (specificity 0,1,1) which
 *     beat the outer CssVarsProvider's `[data-joy-color-scheme="…"]` (0,1,0), so
 *     org colors always win regardless of style injection order.
 *  2. Exposes logo URLs and theme settings via context.
 *
 * The full branding response (including theme_config) is cached in localStorage
 * so CSS variables are injected synchronously on first render, eliminating the
 * flash of default colors on page refresh.
 *
 * A nested CssVarsProvider is mounted when either (a) the org settings specify
 * a locked color mode (not "system"), or (b) the org's runtime brand scheme
 * (spec R31) differs from the build-time deployment scheme. The resolved scheme
 * theme is passed so the base brand palette swaps at runtime; when neither
 * applies the provider is omitted and rendering is byte-identical to before.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { CssVarsProvider, useColorScheme } from "@mui/joy/styles";
import {
  OrgBrandingResponse,
  OrgThemeConfig,
  OrgThemeSettings,
  PaletteShades,
} from "../common/types";
import { useGetOrgBranding, useReadUser } from "../services/api";
import {
  resolveBrandScheme,
  deploymentSchemeName,
  themesRegistry,
} from "../theme/themes";
import { buildOrgTheme, buildCssVarBlock } from "../theme/buildOrgTheme";

// ---------------------------------------------------------------------------
// Branding cache — persisted in localStorage so the full branding response
// (including theme_config) is available on the first render after a page
// refresh, before the async fetch completes.
// ---------------------------------------------------------------------------

const BRANDING_CACHE_KEY = "mm_org_branding_cache";

interface BrandingCache {
  branding: OrgBrandingResponse;
  version: number;
}

function loadBrandingCache(): BrandingCache | null {
  try {
    const stored = localStorage.getItem(BRANDING_CACHE_KEY);
    if (stored) return JSON.parse(stored) as BrandingCache;
  } catch {}
  return null;
}

function saveBrandingCache(branding: OrgBrandingResponse, version: number): void {
  try {
    localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify({ branding, version }));
  } catch {}
}

// ---------------------------------------------------------------------------
// CSS variable injection
// ---------------------------------------------------------------------------

function buildOrgCssVars(config: OrgThemeConfig): string {
  return buildCssVarBlock(buildOrgTheme(config), "html");
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface OrgThemeContextValue {
  logoLightUrl: string | null;
  logoDarkUrl: string | null;
  brandingAssetVersion: number;
  themeSettings: OrgThemeSettings;
  hasBranding: boolean;
  refreshBranding: () => void;
  updateBranding: (data: OrgBrandingResponse) => void;
}

const defaultSettings: OrgThemeSettings = {
  allow_user_theme_override: true,
  default_mode: "system",
};

const normalizePaletteShades = (shades: PaletteShades | null | undefined): PaletteShades | undefined =>
  shades ?? undefined;

const normalizeThemeConfig = (config: OrgThemeConfig | null | undefined): OrgThemeConfig | null => {
  if (!config) return null;

  return {
    ...config,
    light: {
      ...config.light,
      palette: {
        ...config.light?.palette,
        primary: normalizePaletteShades(config.light?.palette?.primary as PaletteShades | null | undefined),
        neutral: normalizePaletteShades(config.light?.palette?.neutral as PaletteShades | null | undefined),
        success: normalizePaletteShades(config.light?.palette?.success as PaletteShades | null | undefined),
        warning: normalizePaletteShades(config.light?.palette?.warning as PaletteShades | null | undefined),
        danger: normalizePaletteShades(config.light?.palette?.danger as PaletteShades | null | undefined),
      },
    },
    dark: {
      ...config.dark,
      palette: {
        ...config.dark?.palette,
        primary: normalizePaletteShades(config.dark?.palette?.primary as PaletteShades | null | undefined),
        neutral: normalizePaletteShades(config.dark?.palette?.neutral as PaletteShades | null | undefined),
        success: normalizePaletteShades(config.dark?.palette?.success as PaletteShades | null | undefined),
        warning: normalizePaletteShades(config.dark?.palette?.warning as PaletteShades | null | undefined),
        danger: normalizePaletteShades(config.dark?.palette?.danger as PaletteShades | null | undefined),
      },
    },
    settings: config.settings ?? defaultSettings,
  };
};

const OrgThemeContext = createContext<OrgThemeContextValue>({
  logoLightUrl: null,
  logoDarkUrl: null,
  brandingAssetVersion: 0,
  themeSettings: defaultSettings,
  hasBranding: false,
  refreshBranding: () => {},
  updateBranding: () => {},
});

export const useOrgTheme = () => useContext(OrgThemeContext);

// ---------------------------------------------------------------------------
// Inner wrapper — applies mode lock when override is disabled
// ---------------------------------------------------------------------------

const ModeLock: React.FC<{ settings: OrgThemeSettings; children: React.ReactNode }> = ({
  settings,
  children,
}) => {
  const { setMode } = useColorScheme();

  useEffect(() => {
    if (!settings.allow_user_theme_override && settings.default_mode !== "system") {
      setMode(settings.default_mode);
    }
  }, [settings.allow_user_theme_override, settings.default_mode, setMode]);

  return <>{children}</>;
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface OrgThemeProviderProps {
  children: React.ReactNode;
}

export const OrgThemeProvider: React.FC<OrgThemeProviderProps> = ({ children }) => {
  const readUser = useReadUser();
  const getOrgBranding = useGetOrgBranding();

  const [branding, setBranding] = useState<OrgBrandingResponse | null>(() => {
    const cached = loadBrandingCache();
    if (!cached) return null;
    return { ...cached.branding, theme_config: normalizeThemeConfig(cached.branding.theme_config) };
  });
  const [brandingAssetVersion, setBrandingAssetVersion] = useState<number>(
    () => loadBrandingCache()?.version ?? 0,
  );
  const [orgId, setOrgId] = useState<string | null>(null);

  const fetchBranding = useCallback(
    async (id: string) => {
      try {
        const data = await getOrgBranding(id);
        const version = Date.now();
        const normalized = { ...data, theme_config: normalizeThemeConfig(data.theme_config) };
        setBranding(normalized);
        setBrandingAssetVersion(version);
        saveBrandingCache(normalized, version);
      } catch {
        // Silently ignore — fall back to build-time theme
      }
    },
    [getOrgBranding],
  );

  // Fetch user once to get org_id
  useEffect(() => {
    readUser()
      .then((user) => {
        if (user?.organization_id) {
          setOrgId(user.organization_id);
          fetchBranding(user.organization_id);
        }
      })
      .catch(() => {
        // Not authenticated — no branding needed yet
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshBranding = useCallback(() => {
    if (orgId) fetchBranding(orgId);
  }, [orgId, fetchBranding]);

  const updateBranding = useCallback((data: OrgBrandingResponse) => {
    const version = Date.now();
    const normalized = { ...data, theme_config: normalizeThemeConfig(data.theme_config) };
    setBranding(normalized);
    setBrandingAssetVersion(version);
    saveBrandingCache(normalized, version);
  }, []);

  const themeSettings: OrgThemeSettings = branding?.theme_config?.settings ?? defaultSettings;
  const hasBranding =
    branding !== null &&
    (branding.theme_config !== null ||
      branding.logo_light_url !== null ||
      branding.logo_dark_url !== null);

  const ctxValue: OrgThemeContextValue = {
    logoLightUrl: branding?.logo_light_url ?? null,
    logoDarkUrl: branding?.logo_dark_url ?? null,
    brandingAssetVersion,
    themeSettings,
    hasBranding,
    refreshBranding,
    updateBranding,
  };

  const orgCssVars = branding?.theme_config
    ? buildOrgCssVars(branding.theme_config)
    : null;

  const defaultMode =
    themeSettings.default_mode === "system" ? undefined : themeSettings.default_mode;

  // Per-org RUNTIME brand-scheme selection (spec R31 / 7.6). The backend emits a
  // top-level `brand_scheme` on the branding response (closed set, validated by
  // the PUT; null or omitted means the deployment default). When a per-org scheme
  // differs from the build-time deployment scheme, the resolved theme object
  // carries that scheme's true brand palette (MetalMind orange #f59e0b vs SSI
  // ocean blue #009ccf) and is applied by mounting a nested CssVarsProvider below.
  // Any org palette hex ramps in theme_config are layered on top of the resolved
  // scheme by the orgCssVars <style> block.
  const orgSchemeName = branding?.brand_scheme ?? null;
  const activeScheme = resolveBrandScheme(orgSchemeName);
  const schemeDiffersFromDeployment =
    activeScheme !== themesRegistry[deploymentSchemeName];

  const wrappedChildren = (
    <OrgThemeContext.Provider value={ctxValue}>
      <ModeLock settings={themeSettings}>{children}</ModeLock>
    </OrgThemeContext.Provider>
  );

  const content = orgCssVars ? (
    <>
      <style dangerouslySetInnerHTML={{ __html: orgCssVars }} />
      {wrappedChildren}
    </>
  ) : wrappedChildren;

  // A nested CssVarsProvider is mounted when either (a) the org locks a non-
  // system color mode, or (b) the org's runtime brand scheme differs from the
  // build-time deployment scheme. In both cases the resolved scheme theme is
  // passed so the base brand palette swaps at runtime; when neither applies the
  // provider is omitted and rendering is byte-identical to before this change.
  if (defaultMode || schemeDiffersFromDeployment) {
    return (
      <CssVarsProvider theme={activeScheme} defaultMode={defaultMode}>
        {content}
      </CssVarsProvider>
    );
  }

  return content;
};
